import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoenixMark } from "@/components/Logo";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate(location.state?.from || "/dashboard");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#050505]">
      <div className="hidden lg:block relative">
        <img src="https://images.pexels.com/photos/3998407/pexels-photo-3998407.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />
        <div className="absolute bottom-12 left-12 max-w-sm">
          <h2 className="font-display text-3xl font-bold">The barber quality network.</h2>
          <p className="mt-3 text-neutral-300">Where craft becomes status, leads, and income.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-2 mb-10"><PhoenixMark className="h-10 w-10" /><span className="font-display text-xl font-bold">Barb<span className="gold-text">istry</span></span></Link>
          <h1 className="font-display text-3xl font-bold">Log in</h1>
          <p className="mt-2 text-sm text-neutral-400">Welcome back to Barbistry.</p>

          <Button onClick={googleLogin} data-testid="google-login-btn" variant="outline" className="mt-8 w-full rounded-none h-11 bg-white text-black hover:bg-neutral-200 border-none font-medium">
            Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-6 text-xs text-neutral-600"><div className="flex-1 h-px bg-white/10" />OR<div className="flex-1 h-px bg-white/10" /></div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-neutral-300">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="mt-1.5 bg-[#111] border-white/10 rounded-none focus-visible:ring-[#D4AF37]" />
            </div>
            <div>
              <Label className="text-neutral-300">Password</Label>
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="mt-1.5 bg-[#111] border-white/10 rounded-none focus-visible:ring-[#D4AF37]" />
            </div>
            {err && <p className="text-sm text-red-400" data-testid="login-error">{err}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full rounded-none h-11 bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold">{loading ? "Logging in…" : "Log in"}</Button>
          </form>

          <p className="mt-6 text-sm text-neutral-400">New here? <Link to="/register" className="text-[#D4AF37] hover:underline">Join Barbistry</Link></p>
        </div>
      </div>
    </div>
  );
}
